import type { EnvironmentProviders } from '@angular/core';
import { isDevMode } from '@angular/core';
import { provideState, provideStore } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { provideRouterStore, RouterState } from '@ngrx/router-store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { getMe, setInterfaceLanguage } from './effects/user-settings.effect';
import {
  getUserProfile,
  logout,
  redirectAfterProfileLoaded,
  setStoreSession,
} from './effects/user.effects';
import {
  socialLoginByCode,
  startLoadRequiredData,
} from './effects/auth.effects';
import { organizationFeature } from './state/organization/organization.feature';
import {
  loadActiveOrganization,
  pressSave$,
  updateOrganizationSettings,
  leaveOrg,
  deleteOrg,
} from './effects/organization.effect';
import { userFeature } from './state/user/user.feature';
import { userSettingsFeature } from './state/user-settings/user-settings.feature';
import { connectivityFeature } from './state/connectivity/connectivity.feature';
import {
  inactivityTicks$,
  logoutCleanup$,
  registerUserActivity$,
} from './effects/connectivity.effects';
import { permissionsFeature } from './state/permissions/permissions.feature';
import { getPermissions$ } from './effects/permissions.effects';
import { violationsFeature } from './state/violations/violations.feature';
import { startLoadViolations$, getViolations$ } from './effects/violations.effects';
import { routeFeature } from './state/route/route.feature';
import { routerEvents$ } from './effects/router.effects';
import { menuFeature } from './state/menu/menu.feature';
import { sharedFeature } from './state/shared/shared.feature';
import { confirmation$, showConfirmNameModal$, showMessage$ } from './effects/shared.effects';

export const provideAppState = (): EnvironmentProviders[] => [
  provideStore({}),
  provideState(connectivityFeature),
  provideState(userFeature),
  provideState(userSettingsFeature),
  provideState(organizationFeature),
  provideState(permissionsFeature),
  provideState(violationsFeature),
  provideState(routeFeature),
  provideState(menuFeature),
  provideState(sharedFeature),
  provideEffects({
    getMe,
    setInterfaceLanguage,
  }),
  provideEffects({
    loadActiveOrganization,
    pressSave$,
    updateOrganizationSettings,
    leaveOrg,
    deleteOrg,
  }),
  provideEffects({
    registerUserActivity$,
    inactivityTicks$,
    logoutCleanup$,
  }),
  provideEffects({
    startLoadRequiredData,
    getUserProfile,
    setStoreSession,
    redirectAfterProfileLoaded,
    logout,
    socialLoginByCode,
  }),
  provideEffects({
    getPermissions$,
  }),
  provideEffects({
    startLoadViolations$,
    getViolations$,
  }),
  provideEffects({
    routerEvents$,
  }),
  provideEffects({
    showMessage$,
    confirmation$,
    showConfirmNameModal$,
  }),
  provideRouterStore({
    stateKey: 'router',
    routerState: RouterState.Minimal,
  }),
  provideStoreDevtools({
    maxAge: 25,
    logOnly: !isDevMode(),
    autoPause: true,
    trace: false,
  }),
];
